/**
 * API клієнт для doc.retail_returns (Порція Роздріб.4/5a).
 * Backend: /api/v1/retail/retail-returns.
 *
 * Permissions:
 *  - retail.return.create — POST/PATCH/DELETE/GET
 *  - retail.return.post — /post, /unpost
 *  - retail.return.fiscalize — /fiscalize
 */
import { api } from './client';
import type { Page } from './catalogs';
import type { FiscalStatus } from './retailSales';
import type { CancellableState } from './cancellation';

export type ReturnStatus = 'draft' | 'posted' | 'voided';
export type ReturnMethod = 'cash' | 'card' | 'bonus' | 'mixed';
export type ReturnItemCondition = 'normal' | 'damaged' | 'expired';

export interface RetailReturnItemIn {
  line_number: number;
  original_sale_item_id: string;
  nomenclature_id: string;
  quantity: string;
  unit_id?: string | null;
  price_per_unit: string;
  item_condition?: ReturnItemCondition;
}

export interface RetailReturnItem {
  id: string;
  line_number: number;
  original_sale_item_id: string;
  nomenclature_id: string;
  quantity: string;
  unit_id: string | null;
  price_per_unit: string;
  total: string;
  cost_per_unit: string | null;
  cost_total: string | null;
  item_condition: ReturnItemCondition | null;
}

export interface RetailReturnListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  original_sale_id: string;
  pos_workstation_id: string;
  cashier_user_id: string;
  return_amount: string;
  return_method: ReturnMethod;
  fiscal_status: FiscalStatus;
  fiscal_number: string | null;
  posted: boolean;
  status: ReturnStatus;
  deletion_mark: boolean;
  // Block 5.3c: CancellableMixin state (coexist з retail return status).
  state?: CancellableState;
}

export interface RetailReturn extends RetailReturnListItem {
  cash_amount: string;
  card_amount: string;
  bonus_restore_amount: string;
  return_reason: string | null;
  fiscal_url: string | null;
  fiscalized_at: string | null;
  fiscalization_error: string | null;
  posted_at: string | null;
  created_at: string;
  created_by: string | null;
  comment: string | null;
  items: RetailReturnItem[];
  // Block 5.3c:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface RetailReturnCreate {
  organization_id: string;
  document_number?: string | null;
  document_date?: string | null;
  original_sale_id: string;
  pos_workstation_id: string;
  return_method?: ReturnMethod;
  cash_amount?: string;
  card_amount?: string;
  bonus_restore_amount?: string;
  return_reason?: string | null;
  items: RetailReturnItemIn[];
  comment?: string | null;
}

export interface RetailReturnUpdate {
  document_number?: string | null;
  document_date?: string | null;
  return_reason?: string | null;
  comment?: string | null;
}

export interface RetailReturnFiscalizeOut {
  document: RetailReturn;
  fiscal_status: FiscalStatus;
  fiscal_number: string | null;
  fiscal_url: string | null;
  fiscalized_at: string | null;
  error_message?: string | null;
}

export const retailReturns = {
  list: (params?: {
    organization_id?: string;
    original_sale_id?: string;
    pos_workstation_id?: string;
    fiscal_status?: FiscalStatus;
    posted?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    api.get<Page<RetailReturnListItem>>('/retail/retail-returns', { params }).then((r) => r.data),
  get: (id: string) => api.get<RetailReturn>(`/retail/retail-returns/${id}`).then((r) => r.data),
  create: (payload: RetailReturnCreate) =>
    api.post<RetailReturn>('/retail/retail-returns', payload).then((r) => r.data),
  update: (id: string, payload: RetailReturnUpdate) =>
    api.patch<RetailReturn>(`/retail/retail-returns/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/retail/retail-returns/${id}`).then(() => undefined),
  post: (id: string) => api.post<RetailReturn>(`/retail/retail-returns/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<RetailReturn>(`/retail/retail-returns/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/retail/retail-returns/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
  fiscalize: (id: string) =>
    api.post<RetailReturnFiscalizeOut>(`/retail/retail-returns/${id}/fiscalize`).then((r) => r.data),
};
